import Decimal from '@st/decimal'
import { isTruthy } from '@st/utils'
import { useDepositBonusesStore } from '../stores/useDepositBonusesStore'
import type {
  ExtendedDepositBonus,
  DepositBonusesPreparedChain,
} from '../types'
import { useGetTitleByLevel } from './useGetTitleByLevel'
import { useBonusesDeps } from '../useDeps'

interface UseDepositBonusesChainReturn {
  preparedBonusesChain: ComputedRef<DepositBonusesPreparedChain | null>
}

export function useDepositBonusesChain(): UseDepositBonusesChainReturn {
  const { format: formatCrypto } = useCryptoFormatter()
  const { format: formatMultiplier } = useNumberFormatter()
  const { getCurrencyIconById } = useBonusesDeps()
  const { depositProgramsWithChain } = storeToRefs(useDepositBonusesStore())
  const { t } = useI18n()
  const bonusesChain = computed(() => depositProgramsWithChain.value[0] ?? [])

  const multiplierSum = computed(
    () =>
      `${formatMultiplier(
        bonusesChain.value
          .reduce(
            (sum, item) =>
              sum.plus(
                item.bonusPrograms.bonusSettings?.depositMultiplier ?? 0,
              ),
            new Decimal(0),
          )
          .mul(100)
          .toString(),
      )}%`,
  )
  const maxBonusSum = computed(() =>
    formatCrypto(
      bonusesChain.value
        .reduce(
          (sum, item) =>
            sum.plus(
              item.bonusPrograms.bonusSettings?.maxBonusAmountInBonusCurrency ??
                0,
            ),
          new Decimal(0),
        )
        .toString(),
    ),
  )
  /* если цепочек несколько выводится самая первая */
  const firstChainItem = computed(() => bonusesChain.value[0])

  function createBonusesAwardOutput({
    bonusPrograms: { bonusSettings, freebet, freespinProgram, reloadProgram },
  }: ExtendedDepositBonus) {
    const cashAward = bonusSettings
      ? {
          icon: null,
          text: '',
          amount: `${formatMultiplier(
            new Decimal(bonusSettings.depositMultiplier ?? 0)
              .mul(100)
              .toString(),
          )}%`,
        }
      : null

    const freespinAward = freespinProgram
      ? {
          icon: null,
          text: '',
          amount: `${freespinProgram.freespinNumber} FS`,
        }
      : null
    const freebetAward = freebet
      ? {
          icon: getCurrencyIconById(freebet.currencyId),
          amount: formatCrypto(freebet.amount),
          text: t('bonuses.freebet'),
        }
      : null

    const reloadAward = reloadProgram
      ? {
          icon: getCurrencyIconById(reloadProgram.currencyId),
          amount: formatCrypto(reloadProgram.amount),
          text: t('bonuses.reload'),
        }
      : null
    const filteredMultiBonuses = [
      cashAward,
      freespinAward,
      freebetAward,
      reloadAward,
    ].filter(isTruthy)

    return {
      main: filteredMultiBonuses[0],
      others:
        filteredMultiBonuses.length > 1 ? filteredMultiBonuses.slice(1) : [],
    }
  }
  const { getTitle } = useGetTitleByLevel()

  const preparedBonusesChain = computed(() =>
    bonusesChain.value.length
      ? {
          totalDepositBonusPercent: multiplierSum.value,
          totalDepositBonusAmountInBonusCurrency: maxBonusSum.value,
          bonusCurrencyIcon: getCurrencyIconById(
            firstChainItem.value?.bonusPrograms?.bonusSettings?.currencyId ?? 0,
          ),
          chainLevels: bonusesChain.value.map((item) => ({
            id: item.id,
            level: item.level,
            title: getTitle(t('bonuses.bonus'), item.level),
            isStepBonus: !!item.steps?.length,
            bonusesOutput: createBonusesAwardOutput(item),
            status: item.status,
          })),
        }
      : null,
  )

  return {
    preparedBonusesChain,
  }
}
